export type GalleryWall = "left" | "back" | "right";

export interface GalleryLayoutInput {
  id: string;
  title: string;
  displayOrder: number;
}

export interface GalleryPlacement {
  roomWall: GalleryWall;
  roomX: number;
  roomY: number;
}

export type GalleryLayoutArtwork<T extends GalleryLayoutInput> = T & GalleryPlacement;

export interface SpatialGridPlacement {
  gridColumn: number;
  gridRow: number;
  gridX: number;
  gridY: number;
  gridZ: number;
  gridScale: number;
  gridRotationY: number;
  gridDrift: number;
}

export type SpatialGridArtwork<T extends GalleryLayoutInput> = T & SpatialGridPlacement;

export interface SpatialGridOptions {
  columns?: number;
}

const WALL_SEQUENCE: GalleryWall[] = ["left", "back", "right"];
const WALL_X = [-4.8, -2.4, 0];
const ROW_Y = [2.25, -0.25, -2.75];
const ARTWORKS_PER_BAND = 6;

export function assignGalleryPlacements<T extends GalleryLayoutInput>(
  artworks: T[]
): Array<GalleryLayoutArtwork<T>> {
  return [...artworks]
    .sort((a, b) => a.displayOrder - b.displayOrder || a.title.localeCompare(b.title))
    .map((artwork, index) => ({
      ...artwork,
      roomWall: WALL_SEQUENCE[index % WALL_SEQUENCE.length],
      roomX: WALL_X[index % WALL_X.length],
      roomY: ROW_Y[Math.floor(index / ARTWORKS_PER_BAND) % ROW_Y.length]
    }));
}

export function assignSpatialGridPlacements<T extends GalleryLayoutInput>(
  artworks: T[],
  options: SpatialGridOptions = {}
): Array<SpatialGridArtwork<T>> {
  const columns = options.columns ?? 7;
  const xGap = 3;
  const yGap = 2.25;
  const startX = -((columns - 1) * xGap) / 2;
  const depthPattern = [0, -0.9, -1.7, -0.45];
  const scalePattern = [1, 0.92, 0.84, 0.96];
  const rotationPattern = [-0.18, -0.07, 0.08, 0.18];

  return [...artworks]
    .sort((a, b) => a.displayOrder - b.displayOrder || a.title.localeCompare(b.title))
    .map((artwork, index) => {
      const gridColumn = index % columns;
      const gridRow = Math.floor(index / columns);
      const patternIndex = (gridColumn + gridRow) % depthPattern.length;

      return {
        ...artwork,
        gridColumn,
        gridRow,
        gridX: Number((startX + gridColumn * xGap + (gridRow % 2 ? xGap * 0.5 : 0)).toFixed(2)),
        gridY: Number((1.65 - gridRow * yGap).toFixed(2)),
        gridZ: depthPattern[patternIndex],
        gridScale: scalePattern[patternIndex],
        gridRotationY: rotationPattern[gridColumn % rotationPattern.length],
        gridDrift: Number((((gridRow % 3) - 1) * 0.22 + ((gridColumn % 2) - 0.5) * 0.08).toFixed(2))
      };
    });
}
